import { Component, Input, OnInit, ViewChild, signal } from '@angular/core';
import { UntypedFormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { collection, getDocs, onSnapshot, query, deleteDoc, doc, addDoc } from 'firebase/firestore';
import { getStorage, ref, deleteObject } from 'firebase/storage';
import { dataStore, db } from 'src/app/app.component';
import { LogType } from 'src/app/enums';
import { ucFirst } from 'src/app/globals';
import { Particularity, RidePreparationLog } from 'src/app/interfaces';
import { ManageParticularityComponent } from 'src/app/ride-preparation/dialogs/manage-particularity/manage-particularity.component';
import { WarningDialog } from 'src/app/global-dialogs/warning/warning.component';
import { MatCard } from '@angular/material/card';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatIconButton } from '@angular/material/button';
import { MatRipple } from '@angular/material/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-particularities',
  templateUrl: './particularities.component.html',
  styleUrl: './particularities.component.scss',
  imports: [
    MatCard,
    MatFormField,
    MatIcon,
    MatLabel,
    MatInput,
    FormsModule,
    ReactiveFormsModule,
    MatIconButton,
    MatSuffix,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatRipple,
    MatProgressSpinner,
  ],
})
export class ParticularitiesComponent implements OnInit {
  @Input() parentData: { processing: boolean };
  @ViewChild('table', { static: false }) table: MatTable<any>;

  systemParticularities = signal<Particularity[]>([]);
  particularities = signal<Particularity[]>([]);
  filteredParticularities = signal<Particularity[]>([]);
  totalParticularities = signal<number>(0);
  searchCtrl = new UntypedFormControl();
  displayedColumns = ['icon', 'name', 'description', 'button'];
  organisationId = dataStore.selectedOrganisation.id;

  constructor(
    public dialog: MatDialog,
    public snackBar: MatSnackBar,
  ) {}

  async ngOnInit(): Promise<void> {
    const sysParticularities = (await getDocs(collection(db, 'particularities'))).docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }),
    ) as Particularity[];
    this.systemParticularities.set(sysParticularities);

    onSnapshot(
      query(collection(db, `organisations/${this.organisationId}/particularities`)),
      (snapshot) => {
        const particularities: Particularity[] = [];
        this.totalParticularities.set(snapshot.size);
        snapshot.forEach((doc) => {
          const data = doc.data() as Particularity;
          const id = doc.id;
          data.name = ucFirst(data?.name ?? '');
          particularities.push({ id, ...data });
        });
        this.particularities.set(particularities);
        this.filteredParticularities.set(
          this.filterParticularities(this.searchCtrl.value, particularities),
        );
        if (this.table) {
          this.table.renderRows();
        }
      },
    );

    this.searchCtrl.valueChanges.subscribe((value) => {
      this.filteredParticularities.set(this.filterParticularities(value, this.particularities()));
      if (this.table) {
        this.table.renderRows();
      }
    });
  }

  clearSearch() {
    this.searchCtrl.setValue('');
    this.searchCtrl.updateValueAndValidity();
  }

  filterParticularities(value: string, array: Particularity[]): Particularity[] {
    let filtered = array;
    if (value && value !== '') {
      const filterValue = value.toLowerCase();
      filtered = array.filter((particularity) =>
        particularity.name.toLowerCase().includes(filterValue),
      );
    }
    filtered.sort((a, b) => {
      const aIsSystem = this.isSystemParticularity(a);
      const bIsSystem = this.isSystemParticularity(b);
      if (aIsSystem && !bIsSystem) return -1;
      if (!aIsSystem && bIsSystem) return 1;
      return a.name.localeCompare(b.name);
    });
    return filtered;
  }

  isSystemParticularity(particularity: Particularity): boolean {
    if (!particularity) {
      return false;
    }
    return this.systemParticularities().some((sp) => sp.id === particularity.id);
  }

  editItem(particularity?: Particularity) {
    const dialogRef = this.dialog.open(ManageParticularityComponent, {
      width: '375px',
      data: {
        item: particularity,
        isSystemParticularity: this.isSystemParticularity(particularity),
      },
    });
  }

  async deleteItem(particularity: Particularity) {
    if (this.isSystemParticularity(particularity)) {
      return;
    }
    const dialogRef = this.dialog.open(WarningDialog, {
      width: '500px',
      autoFocus: false,
      data: {
        dialogType: 'deleteParticularity',
        optionalData: {
          particularityName: particularity.name,
          deleteMode: true,
        },
      },
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        try {
          // Delete from DB
          await deleteDoc(
            doc(db, `organisations/${this.organisationId}/particularities/${particularity.id}`),
          );
          await this.logDelete(particularity);

          // Delete from storage if iconUrl
          if (particularity.iconUrl) {
            const storage = getStorage();
            // Extract path from download URL
            const urlParts = particularity.iconUrl.split('/o/');
            if (urlParts.length > 1) {
              const path = decodeURIComponent(urlParts[1].split('?')[0]);
              const storageRef = ref(storage, path);
              await deleteObject(storageRef);
            }
          }

          this.snackBar.open('Bijzonderheid verwijderd.', 'X', { duration: 5000 });
        } catch (error) {
          this.snackBar.open(
            'Er is iets misgegaan tijdens het verwijderen van de bijzonderheid.',
            'X',
            {
              duration: 5000,
            },
          );
        }
      }
    });
  }

  async logDelete(item: Particularity) {
    if (!item) {
      return;
    }

    const logObj: RidePreparationLog = {
      type: LogType.RidePreparationParticularity,
      date: new Date(),
      action: 'delete',
      userEmail: dataStore.rootUser.email,
      userId: dataStore.rootUser.id,
      itemType: 'bijzonderheid',
      itemId: item.id,
      role:
        dataStore.rootUser.rights === 'admin'
          ? 'systemAdmin'
          : dataStore.orgUser?.roles?.join(', ') || 'onbekend',
      before: { ...item },
      after: null,
    };
    delete logObj.before.id;

    await addDoc(
      collection(
        db,
        `eventLogs/${dataStore.selectedOrganisation.id}/changedRidePreparationSettings`,
      ),
      logObj,
    );
  }
}
